import React, { useState, useCallback } from 'react';
import { galleryCategories, galleryItems } from '../mock';
import { ZoomIn } from 'lucide-react';
import GalleryLightbox from './GalleryLightbox';

const GalleryFilters = ({ active, onChange }) => (
  <div className="flex flex-wrap gap-2 md:gap-3 mb-12">
    {galleryCategories.map(c => (
      <button
        key={c.id}
        onClick={() => onChange(c.id)}
        className={`px-5 py-2.5 text-xs tracking-[0.2em] uppercase border transition-colors duration-300 ${
          active === c.id
            ? 'bg-[#3A4A2C] border-[#3A4A2C] text-[#faf7f2]'
            : 'border-[#3A4A2C]/30 text-[#3A4A2C] hover:border-[#3A4A2C]'
        }`}
      >
        {c.label}
      </button>
    ))}
  </div>
);

const GalleryTile = ({ item, featured, onOpen }) => (
  <button
    onClick={onOpen}
    className={`relative group overflow-hidden bg-[#e6ddc7] ${featured ? 'md:row-span-2 md:col-span-2' : ''}`}
  >
    <div className={`overflow-hidden ${featured ? 'h-[280px] md:h-full min-h-[500px]' : 'h-[220px] md:h-[240px]'}`}>
      {item.portrait ? (
        <div className="w-full h-full flex items-center justify-center bg-[#f3ede0]">
          <img
            src={item.src}
            alt={item.title}
            loading="lazy"
            className="max-h-full max-w-full object-contain rotate-90 group-hover:scale-105 transition-transform duration-[1200ms]"
          />
        </div>
      ) : (
        <img
          src={item.src}
          alt={item.title}
          loading="lazy"
          className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-[1200ms]"
        />
      )}
    </div>
    <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
    <div className="absolute inset-0 flex flex-col justify-end p-5 opacity-0 group-hover:opacity-100 transition-opacity duration-500 text-left">
      <span className="text-white/70 text-[10px] tracking-[0.3em] uppercase mb-1">{item.category}</span>
      <h4 className="text-white font-serif text-lg">{item.title}</h4>
    </div>
    <div className="absolute top-4 right-4 w-9 h-9 rounded-full bg-white/0 group-hover:bg-white/95 flex items-center justify-center transition-all duration-300">
      <ZoomIn size={16} className="text-[#3A4A2C] opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
    </div>
  </button>
);

const Gallery = () => {
  const [cat, setCat] = useState('all');
  const [lightbox, setLightbox] = useState(null);

  const items = cat === 'all' ? galleryItems : galleryItems.filter(i => i.category === cat);

  const nav = useCallback((dir) => {
    setLightbox(current => {
      if (!current) return current;
      const currentIndex = items.findIndex(i => i.id === current.id);
      const nextIdx = (currentIndex + dir + items.length) % items.length;
      return items[nextIdx];
    });
  }, [items]);

  return (
    <section id="gallery" className="bg-[#f3ede0] py-24 md:py-32">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between mb-14 gap-6">
          <div>
            <p className="text-[#C87355] text-xs tracking-[0.4em] uppercase mb-5">Galleria</p>
            <h2 className="font-serif text-[#1f1f1f] text-4xl md:text-5xl lg:text-6xl leading-[1.05]">
              Ogni angolo,<br />
              <span className="italic text-[#3A4A2C]">una storia.</span>
            </h2>
          </div>
          <p className="text-[#666] text-base leading-relaxed max-w-md">
            Esplora il casale, la piscina panoramica, gli interni curati nel dettaglio e il giardino segreto con l’ulivo secolare.
          </p>
        </div>

        <GalleryFilters active={cat} onChange={setCat} />

        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 md:gap-4">
          {items.map((it, idx) => (
            <GalleryTile
              key={it.id}
              item={it}
              featured={idx % 7 === 0}
              onOpen={() => setLightbox(it)}
            />
          ))}
        </div>
      </div>

      <GalleryLightbox
        item={lightbox}
        onClose={() => setLightbox(null)}
        onPrev={() => nav(-1)}
        onNext={() => nav(1)}
      />
    </section>
  );
};

export default Gallery;
